import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  Sparkles, X, ChevronRight, ChevronDown,
  Activity, Camera, Heart, Moon, Compass, Shield, Clock, Stars, Brain, Users, Waves
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTypewriter } from '@/hooks/useTypewriter';
import { cn } from '@/lib/utils';
import { useTourController } from '@/hooks/useTourController';

// Map tips to navigation paths (if clickable)
const tipNavigationMap: Record<string, string> = {
  'Choose calming breaths when anxious': '/breathwork?pattern=calm',
  'Energising breaths when sluggish': '/breathwork?pattern=energise',
  'Box breathing for focus and clarity': '/breathwork?pattern=box',
};

// Icon mapping for feature tips (replacing emojis)
const featureIcons: Record<string, React.ComponentType<{ className?: string }>> = {
  'WHOOP': Activity,
  'Sanctuary': Shield,
  'AI Therappi': Sparkles,
  'Timeline': Clock,
  'correlations': Activity,
  'Daily Check-in': Moon,
  'Real-time exercises': Heart,
  'Challenge your thoughts': Brain,
  'Snap photos': Camera,
  'Hormonal': Moon,
  'Future Self': Compass,
  'Spiritual': Stars,
  'therapeutic approach': Brain,
  'QuantumSelf': Sparkles,
  'Connect with your partner': Users,
  'nervous system': Waves,
};

// Get icon for a tip based on keywords
const getIconForTip = (tip: string): React.ComponentType<{ className?: string }> => {
  for (const [keyword, icon] of Object.entries(featureIcons)) {
    if (tip.includes(keyword)) return icon;
  }
  return ChevronRight;
};

interface TherappiWelcomeProps {
  pageKey: string;
  title: string;
  message: string;
  tips?: string[];
  buttonText?: string;
  onDismiss?: () => void;
}

// Storage key prefix for tracking which pages have been seen
const STORAGE_PREFIX = 'therappi_welcome_seen_';

/**
 * Get page-specific welcome content
 * British spelling throughout
 */
export const getWelcomeContent = (pathname: string): { title: string; message: string; tips: string[]; buttonText?: string } | null => {
  // Don't show welcome popup on landing page
  // NOTE: Do NOT block / or /dashboard here - TherappiWelcomeHandler handles that filtering
  // The content is still needed for pre-auth typewriter (called directly from App.tsx)
  if (pathname === '/landing') {
    return null;
  }
  
  // Root path (/) should use dashboard content for post-onboarding welcome
  const effectivePath = pathname === '/' ? '/dashboard' : pathname;
  
  const content: Record<string, { title: string; message: string; tips: string[]; buttonText?: string }> = {
    '/dashboard': {
      title: 'Welcome to Therappi',
      message: "I'm Therappi - your breakthrough AI companion created by Amanda Charles, award-winning Chartered Counselling Psychologist, No. 1 international bestselling Hay House author with 26+ years of clinical experience. This is not just another wellness app. This is the world's first truly intelligent therapeutic platform that sees YOU completely.",
      tips: [
        'WHOOP biometric integration reveals real-time nervous system impact - see exactly how each exercise shifts your HRV, heart rate, and recovery',
        'The Sanctuary - specialised support for anxiety, depression, trauma, OCD and more with 60+ evidence-based exercises including Polyvagal Theory and Somatic Psychology techniques',
        'AI Therappi adapts to you - choose your therapeutic approach: CBT, Person-Centred, Psychotherapy, or QuantumSelf Psychology, our signature multidimensional modality',
        'Your Timeline - every insight, pattern, and breakthrough feeds your AI for a truly personalised approach that remembers your complete history',
        'Connect with your therapist to share wellbeing data and receive better, more informed support between sessions',
        'Connect with your partner and work through challenges together - couples insights, shared check-ins, and relational support for deeper understanding',
        'Powerful correlations show exactly how your wellness practices impact your mood, symptoms, relationships and nervous system over time',
        'Daily Check-in and guided habit transformation - track your progress and build lasting change with intelligent support',
        'Real-time exercises to improve your wellbeing on every level - physical, emotional, mental, relational, and spiritual - with before and after tracking',
        'Spiritual practices for deeper connection - lunar cycle alignment, intuition development, energy work, and manifestation tools grounded in psychology',
        'Challenge your thoughts and enhance relationships - evidence-based CBT tools that create measurable, meaningful shifts',
        'Hormonal cycle tracking for women - work WITH your natural rhythms, not against them, including fertility, pregnancy and menopause support',
        'Future Self Navigator - visualise, connect with, and become who you are meant to be',
        'Snap photos of your meals for instant food-mood-inflammation analysis - discover exactly how what you eat affects how you feel'
      ]
    },
    '/therapy': {
      title: 'AI Therappi Chat',
      message: "Welcome to your Therappi space. This is a confidential place for reflection, support, and growth. There is no judgement here - just understanding, and a safe therapeutic space to explore.",
      tips: [
        "Share whatever is on your mind - I am here to listen",
        "Standard AI uses your daily check-ins, mood tracking, and coping tools to provide supportive, present-focused guidance",
        "I remember our conversations to provide continuity across sessions",
        "Premium AI unlocks deep personalisation - your WHOOP biometrics, cycle data, Timeline history, Sanctuary exercises, and all subscribed modules feed into every response",
        "Premium users get predictive insights, cross-domain pattern analysis, and proactive guidance based on your complete wellness picture"
      ]
    },
    '/daily-check-in': {
      title: 'Daily Check-In',
      message: "Regular check-ins help us spot patterns together. The more you share, the better I can understand what affects your wellbeing.",
      tips: [
        'Morning and evening check-ins are most valuable',
        'Be honest - this is just between us',
        'Your data builds a picture over time'
      ]
    },
    '/daily-checkin': {
      title: 'Daily Check-In',
      message: "Regular check-ins help us spot patterns together. The more you share, the better I can understand what affects your wellbeing.",
      tips: [
        'Morning and evening check-ins are most valuable',
        'Be honest - this is just between us',
        'Your data builds a picture over time'
      ]
    },
    '/nutrition': {
      title: 'Food & Mood',
      message: "What you eat affects how you feel. Track your meals here and I will help you discover the connections between food and mood.",
      tips: [
        'Log meals with photos for AI analysis',
        'Notice how different foods affect your energy',
        'Build awareness of your eating patterns'
      ]
    },
    '/sanctuary': {
      title: 'The Sanctuary × Nervous System',
      message: "Welcome to your nervous system regulation hub. Here you'll find the In-the-Moment Toolkit with breathing, grounding, EFT, meditation, and more - tools for both daily practice and when you're struggling.",
      tips: [
        'Quick Practice mode for daily wellness habits',
        'Need Support mode adapts tools to your distress level',
        'Track what works over time to see your patterns',
        'Regular practice builds lasting resilience'
      ]
    },
    '/breathwork': {
      title: 'Breathwork × Regulation',
      message: "Your breath is a powerful tool for regulating your nervous system. Choose a breathwork style below based on what you need.",
      buttonText: "Choose your breathwork style",
      tips: [
        'Choose calming breaths when anxious',
        'Energising breaths when sluggish',
        'Box breathing for focus and clarity'
      ]
    },
    '/sleep-and-dreams': {
      title: 'Sleep & Dreams × Rest',
      message: "Quality sleep is foundational to wellbeing. Track your rest patterns here and I will help you understand what affects your sleep.",
      tips: [
        'Log sleep quality each morning',
        'Record dreams while fresh in your mind',
        'Notice patterns between sleep and mood'
      ]
    },
    '/whoop-dashboard': {
      title: 'WHOOP × Therappi',
      message: "WHOOP is a wearable fitness tracker that measures your body's physiological signals 24/7 - including heart rate variability (HRV), sleep quality, strain, and recovery. When connected to Therappi, your biometric data transforms into actionable therapeutic insights, showing exactly how your mental health practices affect your body in real-time.",
      tips: [
        'Recovery Score shows how ready your body is for stress - green (67%+) means push yourself, yellow means moderate, red means rest',
        'HRV (Heart Rate Variability) is the gold standard for measuring nervous system health - higher HRV indicates resilience and calm',
        'Sleep Performance reveals true sleep quality beyond just hours - including REM, deep sleep, and sleep efficiency',
        'Strain measures your cardiovascular load through the day - from workouts to stressful meetings',
        'I correlate your WHOOP data with mood, anxiety, and therapy progress to reveal what truly helps you heal',
        'See exactly how breathwork, meditation, and therapy sessions shift your nervous system in real-time'
      ]
    },
    '/mental-wellness': {
      title: 'Mental Wellness × Mood',
      message: "Tracking your emotional landscape builds self-awareness. This is where we monitor your mental health journey together.",
      tips: [
        'Regular tracking reveals patterns',
        'Notice what triggers different moods',
        'Celebrate small improvements'
      ]
    },
    '/future-self-navigator': {
      title: 'Future Self × Visualisation',
      message: "Connecting with your ideal future self builds motivation and clarity. Let us explore who you are becoming.",
      tips: [
        'Visualise your future self clearly',
        'Write letters to and from your future self',
        'Use this for decision-making guidance'
      ]
    },
    '/future-self': {
      title: 'Future Self × Visualisation',
      message: "Connecting with your ideal future self builds motivation and clarity. Let us explore who you are becoming.",
      tips: [
        'Visualise your future self clearly',
        'Write letters to and from your future self',
        'Use this for decision-making guidance'
      ]
    },
    '/future-self-spotlight': {
      title: 'Future Self × Spotlight',
      message: "Connect deeply with your future self here. This builds motivation and clarity about who you are becoming.",
      tips: [
        'Visualise your future self clearly',
        'Write letters to and from your future self',
        'Use this for decision-making guidance'
      ]
    },
    '/insights': {
      title: 'AI Insights × Patterns',
      message: "Here is where the magic happens - I analyse all your data to find meaningful patterns and correlations.",
      tips: [
        'More data means better insights',
        'Premium users get predictive analysis',
        'Check back weekly for new discoveries'
      ]
    },
    '/connect-and-grow': {
      title: 'Connect & Grow × Community',
      message: "Community support accelerates growth. Join challenges, find accountability partners, and share your journey.",
      tips: [
        'Accountability partners boost success',
        'Challenges add fun to habits',
        'Share wins to inspire others'
      ]
    },
    '/community': {
      title: 'Connected Collective × Tribe',
      message: "Welcome to your community space! Connect with others on similar journeys, share experiences, and grow together.",
      tips: [
        'Find your tribe in community circles',
        'Share wins and challenges openly',
        'Support others as they support you'
      ]
    },
    '/relational': {
      title: 'Relational × Connections',
      message: "Our relationships shape our wellbeing. Track and nurture your connections here.",
      tips: [
        'Assess your relationship health regularly',
        'Link with partners for couples insights',
        'I can help you work through challenges together'
      ]
    },
    '/profile': {
      title: 'Your Profile',
      message: "This is where you can customise your Therappi experience. Set your preferences, manage your journeys, and personalise how I support you.",
      tips: [
        'Add your motivations to personalise guidance',
        'Enable journeys relevant to your goals',
        'Review your settings to optimise your experience'
      ]
    },
    '/journal': {
      title: 'Journal × Reflection',
      message: "Your Journal is a private space for reflection. Writing helps process emotions and gain clarity. Let your thoughts flow freely.",
      tips: [
        'Free writing - let thoughts flow without editing or judgement',
        'Worry Diary - capture anxious thoughts, challenge them with CBT techniques, and track patterns over time',
        'Use prompts if you feel stuck or need inspiration',
        'I can help you find themes and patterns across your entries'
      ]
    },
    '/gratitude': {
      title: 'Gratitude × Positivity',
      message: "Gratitude rewires your brain for positivity. Regular practice here can genuinely shift how you experience life.",
      tips: [
        'Log three things you are grateful for daily',
        'Be specific for deeper impact',
        'Review past entries when feeling low'
      ]
    },
    '/exercise': {
      title: 'Movement × Body',
      message: "Physical activity profoundly affects mental health. Track your movement here and I will help you see the connections.",
      tips: [
        'Notice how different exercise affects mood',
        'Even short walks count',
        'Consistency matters more than intensity'
      ]
    },
    '/goals': {
      title: 'Goals & Habits × Growth',
      message: "Setting intentions and building habits transforms your life over time. This is where you shape your future self.",
      tips: [
        'Start with one habit at a time',
        'Track daily for best results',
        'Celebrate every small win'
      ]
    },
    '/cycle-tracker': {
      title: 'Menstruation × Cycle',
      message: "Your hormonal cycle influences mood, energy, and thinking. Understanding it helps you work with your body, not against it.",
      tips: [
        'Log symptoms throughout your cycle',
        'Notice patterns across months',
        'Plan activities around your cycle phases'
      ]
    },
    '/fertility': {
      title: 'Fertility × Conception',
      message: "The fertility journey can be emotionally intense. I am here to support your mental wellbeing alongside the physical journey.",
      tips: [
        'Track treatments and how they affect you',
        'Use this space to process difficult emotions',
        'Connect with partner for shared support'
      ]
    },
    '/longevity': {
      title: 'Longevity × Healthspan',
      message: "Living well is not just about living longer - it is about optimising your healthspan. Track the factors that matter most.",
      tips: [
        'Monitor key biomarkers over time',
        'Small daily habits compound',
        'I will help you connect lifestyle to outcomes'
      ]
    },
    '/inflammation': {
      title: 'Inflammation × Healing',
      message: "Chronic inflammation underlies many health issues. Track and reduce inflammatory factors for better wellbeing.",
      tips: [
        'Log anti-inflammatory foods and habits',
        'Track symptoms that may indicate inflammation',
        'Follow personalised protocols'
      ]
    },
    '/spirituality': {
      title: 'Spirituality × Soul',
      message: "Spirituality connects you to something beyond the everyday. Explore practices that nurture your inner life.",
      tips: [
        'Try different practices to find what resonates',
        'Consistency deepens experience',
        'Journal your spiritual insights'
      ]
    },
    '/intuition': {
      title: 'Intuition × Inner Knowing',
      message: "Your intuition is a valuable inner resource. This space helps you recognise and trust those subtle inner signals.",
      tips: [
        'Log intuitive hits and track accuracy',
        'Notice body sensations with decisions',
        'Practice quieting the analytical mind'
      ]
    },
    '/quantum': {
      title: 'Quantum × Consciousness',
      message: "Explore the frontier where consciousness meets possibility. These practices work with intention and awareness in powerful ways.",
      tips: [
        'Set clear intentions before each practice',
        'Track your manifestation experiments',
        'Stay curious and open'
      ]
    },
    '/grief-support': {
      title: 'Grief × Healing',
      message: "Grief is love with nowhere to go. This is a gentle space to process loss at your own pace, with compassionate support.",
      tips: [
        'There is no right way to grieve',
        'Record memories to honour loved ones',
        'I am here whenever you need to talk'
      ]
    },
    '/mood-food': {
      title: 'Food & Mood × Nutrition',
      message: "What you eat directly impacts how you feel. Here we explore the fascinating relationship between nutrition and emotional wellbeing.",
      tips: [
        'Track meals alongside mood changes',
        'Notice energy patterns after eating',
        'I will highlight your personal food-mood links'
      ]
    },
    '/achievements': {
      title: 'Achievements × Progress',
      message: "Celebrating progress builds motivation. See how far you have come and unlock new milestones as you grow.",
      tips: [
        'Review achievements when motivation dips',
        'Small wins add up to big changes',
        'Share milestones to inspire others'
      ]
    },
    '/habits': {
      title: 'Habits × Transformation',
      message: "Habits are the building blocks of change. Track your daily practices here and watch them transform your life.",
      tips: [
        'Start with just one habit at a time',
        'Link new habits to existing routines',
        'Celebrate every streak, no matter how small'
      ]
    },
    '/values': {
      title: 'Values × Compass',
      message: "Knowing your values is knowing your compass. When you are aligned with what truly matters, decisions become clearer.",
      tips: [
        'Identify your top 5 core values',
        'Notice when you feel out of alignment',
        'Use values to guide difficult decisions'
      ]
    },
    '/life-expansion': {
      title: 'Life Expansion × Stretch',
      message: "Growth happens outside your comfort zone. This is your space to push boundaries and discover new dimensions of yourself.",
      tips: [
        'Start with small stretches',
        'Track how challenges affect your mood',
        'Celebrate the courage to try'
      ]
    },
    '/coaching': {
      title: 'AI Coaching × Action',
      message: "Sometimes you need more than therapy - you need focused guidance toward specific goals. Let us work together on what matters most.",
      tips: [
        'Set clear, measurable goals',
        'I will help you break them into steps',
        'Regular check-ins keep momentum'
      ]
    },
    '/settings': {
      title: 'Settings',
      message: "Customise your Therappi experience here. From notifications to data preferences, make the app work for you.",
      tips: [
        'Set notification times that suit your routine',
        'Review privacy settings for peace of mind',
        'Export your data anytime you need'
      ]
    },
    '/manifestation': {
      title: 'Manifestation × Creation',
      message: "Welcome to your manifestation space! Create vision boards, connect with your future self, and bring your dreams into reality.",
      tips: [
        'Visualise your goals as already achieved',
        'Create a compelling vision board',
        'Connect daily with your future self'
      ]
    },
    '/thought-tracker': {
      title: 'Thought Tracker × CBT',
      message: "Challenge and reframe unhelpful thoughts using evidence-based CBT techniques. Change your thinking, change your life.",
      tips: [
        'Catch negative thoughts as they happen',
        'Identify thinking patterns',
        'Create balanced alternative thoughts'
      ]
    },
    '/mental-tools': {
      title: 'Mental Tools × Techniques',
      message: "Access powerful psychological tools for managing your mind. From CBT to mindfulness, find what works for you.",
      tips: [
        'Explore different techniques',
        'Practice regularly for best results',
        'Track which tools help most'
      ]
    },
    '/vault': {
      title: 'The Vault × Timeline',
      message: "Your complete wellness timeline lives here. Explore correlations, review your journey, and discover patterns.",
      tips: [
        'Browse your complete history',
        'Explore correlations between different areas',
        'Export data anytime you need'
      ]
    },
    '/my-scores': {
      title: 'My Scores × Metrics',
      message: "See all your wellness metrics in one place. Understand what influences your current state.",
      tips: [
        'Check scores regularly',
        'Notice what affects each metric',
        'Use insights to guide your day'
      ]
    },
    '/trends': {
      title: 'Trends × Analytics',
      message: "See how your wellness metrics change over time. Spot patterns and celebrate your progress.",
      tips: [
        'Compare week-over-week changes',
        'Identify what drives improvements',
        'Celebrate positive trends'
      ]
    },
    '/all-tools': {
      title: 'All Tools × Features',
      message: "Every Therappi feature in one place. Explore and find the tools that resonate with you.",
      tips: [
        'Bookmark your favourites',
        'Try something new each week',
        'Ask me about any tool you see'
      ]
    },
    '/crisis': {
      title: 'Crisis × Immediate Help',
      message: "You're not alone. This is a safe space with immediate support tools and resources when things feel overwhelming.",
      tips: [
        'Breathing exercises can help right now',
        'Grounding techniques bring you back',
        'Professional help is always available'
      ]
    },
    '/rock-remedies': {
      title: 'Rock Remedies × Natural',
      message: "Explore Bach Flower Remedies and crystal healing to support your emotional wellbeing naturally.",
      tips: [
        'Select remedies based on emotional states',
        'Combine complementary remedies',
        'Track how different remedies help'
      ]
    },
    '/account': {
      title: 'My Account',
      message: "Manage your profile, preferences, and account settings here.",
      tips: [
        'Keep your profile up to date',
        'Review connected accounts',
        'Manage your privacy settings'
      ]
    },
    '/subscription': {
      title: 'Subscription × Plans',
      message: "View and manage your Therappi subscription here. Unlock more features to deepen your wellness journey.",
      tips: [
        'Review available plans',
        'See what features unlock',
        'Manage billing preferences'
      ]
    },
    '/clinician': {
      title: 'Clinician × Practice',
      message: "Welcome to your clinician dashboard. Manage clients, view insights, and provide enhanced therapeutic support.",
      tips: [
        'View client wellness data',
        'Track progress between sessions',
        'Send personalised prompts'
      ]
    },
    '/clinician-portal': {
      title: 'Clinician × Practice',
      message: "Welcome to your clinician dashboard. Manage clients, view insights, and provide enhanced therapeutic support.",
      tips: [
        'View client wellness data',
        'Track progress between sessions',
        'Send personalised prompts'
      ]
    },
    '/client-portal': {
      title: 'My Therapist × Connection',
      message: "Connect with your therapist through Therappi. Share wellness data for more informed support between sessions.",
      tips: [
        'Choose what data to share',
        'View homework from your therapist',
        'Stay connected between sessions'
      ]
    },
    '/my-therapist': {
      title: 'My Therapist × Connection',
      message: "Connect with your therapist through Therappi. Share wellness data for more informed support between sessions.",
      tips: [
        'Choose what data to share',
        'View homework from your therapist',
        'Stay connected between sessions'
      ]
    },
  };

  return content[effectivePath] || null;
};

/**
 * Check if welcome has been shown for this page
 */
export const hasSeenWelcome = (pageKey: string): boolean => {
  try {
    return localStorage.getItem(`${STORAGE_PREFIX}${pageKey}`) === 'true';
  } catch {
    return false;
  }
};

/**
 * Mark welcome as seen for this page
 */
export const markWelcomeSeen = (pageKey: string): void => {
  try {
    localStorage.setItem(`${STORAGE_PREFIX}${pageKey}`, 'true');
  } catch {
    // localStorage not available
  }
};

/**
 * Reset all welcome states (useful for testing)
 */
export const resetAllWelcomes = (): void => {
  try {
    Object.keys(localStorage)
      .filter(key => key.startsWith(STORAGE_PREFIX))
      .forEach(key => localStorage.removeItem(key));
  } catch {
    // localStorage not available
  }
};

// Typewriter component for individual tips - each instance manages its own state
const TipTypewriter: React.FC<{ 
  text: string; 
  onComplete?: () => void;
  icon: React.ComponentType<{ className?: string }>;
  tipIndex: number;
  currentTipIndex: number;
  onClick?: () => void;
  isClickable?: boolean;
  onUpdate?: () => void;
}> = ({ text, onComplete, icon: Icon, tipIndex, currentTipIndex, onClick, isClickable, onUpdate }) => {
  const tipRef = React.useRef<HTMLDivElement>(null);
  const [displayText, setDisplayText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isFinished, setIsFinished] = useState(false);
  const intervalRef = React.useRef<NodeJS.Timeout | null>(null);
  const onCompleteRef = React.useRef(onComplete);
  const onUpdateRef = React.useRef(onUpdate);
  
  // Keep refs updated
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);
  
  useEffect(() => {
    onUpdateRef.current = onUpdate;
  }, [onUpdate]);
  
  const isActive = tipIndex === currentTipIndex;
  const hasPassed = tipIndex < currentTipIndex;
  
  // Start typewriter when this specific tip becomes the current one
  useEffect(() => {
    // Clean up any existing interval first
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    
    // If this tip is now active and hasn't finished, start typing
    if (isActive && !isFinished) {
      setIsTyping(true);
      setDisplayText('');
      
      let charIndex = 0;
      const speed = 28; // Slower for better reading
      
      intervalRef.current = setInterval(() => {
        if (charIndex < text.length) {
          charIndex++;
          setDisplayText(text.slice(0, charIndex));
          onUpdateRef.current?.();
        } else {
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
          setIsTyping(false);
          setIsFinished(true);
          onCompleteRef.current?.();
        }
      }, speed);
    }
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isActive, isFinished, text]);

  // If we've passed this tip (user moved on), show full text
  useEffect(() => {
    if (hasPassed && !isFinished) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      setDisplayText(text);
      setIsTyping(false);
      setIsFinished(true);
    }
  }, [hasPassed, isFinished, text]);

  // Show full text if finished, otherwise show typed portion
  const textToShow = isFinished ? text : displayText;
  const showCursor = isTyping;

  const content = (
    <>
      <div className={cn(
        "w-9 h-9 rounded-full flex items-center justify-center shrink-0 mt-0.5",
        isClickable ? "bg-primary/20" : "bg-primary/10"
      )}>
        <Icon className="w-4 h-4 text-primary" />
      </div>
      <span className={cn(
        "text-sm leading-relaxed",
        isClickable ? "text-primary underline underline-offset-2" : "text-foreground"
      )}>
        {textToShow}
        {showCursor && (
          <span className="inline-block w-0.5 h-3.5 bg-primary ml-0.5 animate-pulse" />
        )}
      </span>
    </>
  );

  if (isClickable && onClick) {
    return (
      <div ref={tipRef}>
        <button 
          onClick={onClick}
          className="flex items-start gap-4 py-4 w-full text-left hover:bg-primary/5 rounded-lg transition-colors -mx-2 px-2"
        >
          {content}
        </button>
      </div>
    );
  }

  return (
    <div ref={tipRef} className="flex items-start gap-4 py-4">
      {content}
    </div>
  );
};

export const TherappiWelcome: React.FC<TherappiWelcomeProps> = ({
  pageKey,
  title,
  message,
  tips = [],
  buttonText,
  onDismiss
}) => {
  const navigate = useNavigate();
  const tourController = useTourController();
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  const tourPromptRef = React.useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [showTips, setShowTips] = useState(false);
  const [currentTipIndex, setCurrentTipIndex] = useState(-1);
  const [messageComplete, setMessageComplete] = useState(false);
  
  // Check if this is the dashboard page welcome (triggers tour after)
  const isHomePage = pageKey === '/dashboard' || pageKey === '/';

  // Scroll to bottom of tips container during typewriter - aggressive double-scroll
  const scrollTipsToBottom = useCallback(() => {
    if (scrollContainerRef.current) {
      // Immediate scroll first
      scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
      // Then smooth scroll in next frame to catch any layout shifts
      requestAnimationFrame(() => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
        }
      });
    }
  }, []);

  // Scroll tour prompt into view when it appears - ensure it's fully visible
  useEffect(() => {
    if ((currentTipIndex >= tips.length - 1) && showTips && tourPromptRef.current) {
      // Multiple attempts to ensure visibility
      const scrollToPrompt = () => {
        if (tourPromptRef.current) {
          // Scroll the outer container if needed
          tourPromptRef.current.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center' 
          });
        }
      };
      
      // First attempt after short delay
      const timer1 = setTimeout(scrollToPrompt, 400);
      // Second attempt to ensure it worked
      const timer2 = setTimeout(scrollToPrompt, 800);
      // Third attempt for safety
      const timer3 = setTimeout(scrollToPrompt, 1200);
      
      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
        clearTimeout(timer3);
      };
    }
  }, [currentTipIndex, tips.length, showTips]);

  // Memoize onComplete to prevent useTypewriter effect from resetting on every render
  const handleTypewriterComplete = useCallback(() => {
    setMessageComplete(true);
  }, []);

  const { displayedText, isComplete } = useTypewriter({
    text: message,
    speed: 20,
    delay: 300,
    onComplete: handleTypewriterComplete
  });

  // Handle revealing tips one by one
  const handleShowFeatures = () => {
    setShowTips(true);
    setCurrentTipIndex(0);
  };

  const handleTipComplete = useCallback(() => {
    // Longer delay between tips for reading time
    setTimeout(() => {
      setCurrentTipIndex(prev => prev + 1);
    }, 1200);
  }, []);

  useEffect(() => {
    // Check if already seen
    if (!hasSeenWelcome(pageKey)) {
      // Small delay before showing
      const timer = setTimeout(() => {
        setIsVisible(true);
        // Play pop sound when welcome appears
        try {
          const popSound = new Audio('/sounds/therappi-guide-pop.wav');
          popSound.volume = 0.5;
          popSound.play().catch(() => {});
        } catch {
          // Ignore audio errors
        }
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [pageKey]);

  const handleDismiss = (dontShowAgain: boolean, startTour: boolean = false) => {
    if (dontShowAgain) {
      markWelcomeSeen(pageKey);
    }
    setIsVisible(false);
    
    // For home page, trigger the app tour after dismissing
    if (startTour && isHomePage) {
      // CRITICAL: Prepare tour FIRST to block all guards during transition
      tourController.prepareTour();
      console.log('[TherappiWelcome] Prepared tour - guards blocked, starting tour after delay');
      setTimeout(() => {
        tourController.startTour();
      }, 300);
    }
    
    onDismiss?.();
  };

  if (!isVisible) return null;

  return (
    <AnimatePresence>
      {isVisible && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50"
            onClick={() => handleDismiss(true)}
          />

          {/* Bottom Sheet (mobile) / Modal (desktop) */}
          <motion.div
            initial={{ opacity: 0, y: '100%' }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className={cn(
              "fixed z-50 bg-card border border-border shadow-2xl",
              // Mobile: bottom sheet with max height to prevent overflow
              "inset-x-0 bottom-0 rounded-t-3xl max-h-[85vh] flex flex-col",
              // Desktop: centered modal
              "md:inset-auto md:left-1/2 md:top-1/2 md:-translate-x-1/2 md:-translate-y-1/2",
              "md:rounded-2xl md:max-w-md md:w-full md:max-h-[80vh]"
            )}
          >
            {/* Drag handle (mobile) */}
            <div className="md:hidden flex justify-center pt-3 shrink-0">
              <div className="w-12 h-1.5 rounded-full bg-muted-foreground/30" />
            </div>

            <div className="p-6 pb-8 overflow-y-auto flex-1">
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <Sparkles className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h2 className="text-lg font-medium text-foreground">{title}</h2>
                    <p className="text-xs text-muted-foreground">Therappi<sup className="text-[0.6em] align-super">®</sup> Guide</p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDismiss(true)}
                  className="h-8 w-8 text-muted-foreground"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>

              {/* Message with typewriter effect - always visible once typed */}
              <div className="bg-card border border-border rounded-2xl p-4 mb-4 min-h-[80px]">
                <p className="text-sm sm:text-base text-foreground leading-relaxed font-medium">
                  {displayedText}
                  {!isComplete && (
                    <span className="inline-block w-0.5 h-4 bg-primary ml-0.5 animate-pulse" />
                  )}
                </p>
              </div>

              {/* Discover button - shows after message is complete */}
              <AnimatePresence>
                {messageComplete && !showTips && tips.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="mb-4"
                  >
                    <Button
                      variant="gradient"
                      className="w-full gap-2"
                      onClick={handleShowFeatures}
                    >
                      <span>{buttonText || "Discover what Therappi offers"}</span>
                      <ChevronDown className="w-4 h-4" />
                    </Button>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Tips - revealed one by one with typewriter */}
              <AnimatePresence>
                {showTips && tips.length > 0 && (
                  <motion.div
                    ref={scrollContainerRef}
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="mb-4 max-h-[320px] overflow-y-auto scrollbar-thin"
                  >
                    <div className="divide-y divide-border/50">
                      {tips.map((tip, index) => (
                        index <= currentTipIndex && (
                          <motion.div
                            key={index}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                          >
                            <TipTypewriter
                              text={tip}
                              icon={getIconForTip(tip)}
                              tipIndex={index}
                              currentTipIndex={currentTipIndex}
                              onComplete={index < tips.length - 1 ? handleTipComplete : undefined}
                              onUpdate={index === currentTipIndex ? scrollTipsToBottom : undefined}
                              isClickable={!!tipNavigationMap[tip]}
                              onClick={tipNavigationMap[tip] ? () => {
                                handleDismiss(false);
                                navigate(tipNavigationMap[tip]);
                              } : undefined}
                            />
                          </motion.div>
                        )
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Access hint - shows after all tips are revealed */}
              <AnimatePresence>
                {(currentTipIndex >= tips.length - 1 || tips.length === 0) && showTips && (
                  <motion.div
                    ref={tourPromptRef}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-center gap-3 p-3 rounded-xl bg-primary/5 border border-primary/10 mb-4"
                  >
                    <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center shrink-0">
                      <Sparkles className="w-5 h-5 text-primary-foreground" />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {isHomePage ? (
                        <>
                          <span className="text-foreground font-medium">Ready for a quick tour?</span> Let me show you around so you can find everything easily.
                        </>
                      ) : (
                        <>
                          <span className="text-foreground font-medium">I am always here</span> - tap the{' '}
                          <span className="inline-flex items-center gap-1 text-primary">
                            <Sparkles className="w-3 h-3" /> sparkle
                          </span>{' '}
                          button anytime you need guidance.
                        </>
                      )}
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Actions */}
              <div className="flex gap-2">
                {isHomePage && (currentTipIndex >= tips.length - 1 || tips.length === 0) && showTips ? (
                  <>
                    <Button
                      variant="outline"
                      className="flex-1 text-sm"
                      onClick={() => handleDismiss(true, false)}
                    >
                      Skip Tour
                    </Button>
                    <Button
                      variant="gradient"
                      className="flex-1 text-sm"
                      onClick={() => handleDismiss(true, true)}
                    >
                      Show Me Around
                    </Button>
                  </>
                ) : (
                  <Button
                    variant="gradient"
                    className="flex-1 text-sm"
                    onClick={() => handleDismiss(true, isHomePage)}
                  >
                    Got it
                  </Button>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
